import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  VideoPageHelperDialogActions,
  VideoPageHelperDialogButtons,
  VideoPageHelperPaper,
  VideoPageHelperTitle,
  VideoWrapper,
} from './VideoPageHelperStyled';
import { FontAwesomeSpriteIcon } from 'src/modules/shared/components/FontAwesomeLocal';
import { SpriteIconsLight, SpriteIconsSolid } from 'src/config/api/models/FontAwesome';
import theme from 'src/config/theme';
import { VideoPlayer } from 'src/modules/trajectory/components/TrajectoryDetail/blocks/VideoPlayer';
import { PlataformGuidePlace } from 'src/store/plataform-guide/types';
import { useGetPlataformGuideQuery } from 'src/store/plataform-guide/PlataformGuideApi';
import { Collapse, Typography } from '@material-ui/core';
import ReactPlayer from 'react-player';
import { useRequestMeMutation } from 'src/modules/shared/slices/auth/entity/api/auth';
import { LogActionTypes, useDataLogging } from 'src/modules/shared/data-logging';

export const VideoPageHelper: FC<{ place: PlataformGuidePlace }> = ({ place }) => {
  const log = useDataLogging();
  const [isMinimezed, setIsMinimezed] = useState<boolean>(false);
  const [hasAlredyAutoplayed, setHasAlredyAutoplayed] = useState<boolean>(false);
  const playerRef = useRef<ReactPlayer | null>(null);

  const [, { data: meResponse }] = useRequestMeMutation({
    fixedCacheKey: '/me',
  });

  const { data, originalArgs } = useGetPlataformGuideQuery(
    { place },
    { refetchOnMountOrArgChange: true, skip: !!meResponse?.stepper?.key }
  );

  const handleLog = useCallback(
    (state: boolean) => {
      if (state) {
        log(LogActionTypes.PLATFORM_GUIDE_CLOSED, { place });
      } else {
        log(LogActionTypes.PLATFORM_GUIDE_OPEN, { place });
      }
    },
    [log, place]
  );

  const handleMinimezed = useCallback(() => {
    if (!isMinimezed && playerRef.current) {
      const internalPlayer =
        playerRef.current && (playerRef.current.getInternalPlayer() as HTMLVideoElement | null);
      internalPlayer?.pause();
    }
    if (!hasAlredyAutoplayed) {
      setHasAlredyAutoplayed(true);
    }
    setIsMinimezed((prev) => {
      handleLog(!prev);
      return !prev;
    });
  }, [isMinimezed, setIsMinimezed, handleLog, hasAlredyAutoplayed]);

  const shouldAutoPlay = useMemo(() => {
    return data && originalArgs?.place === place && data?.isFirstTime && !hasAlredyAutoplayed;
  }, [data, hasAlredyAutoplayed, originalArgs?.place, place]);

  useEffect(() => {
    if (!shouldAutoPlay) return;
    if (!isMinimezed) return;
    if (shouldAutoPlay) {
      setIsMinimezed(false);
    }
  }, [isMinimezed, shouldAutoPlay, hasAlredyAutoplayed]);

  useEffect(() => {
    setHasAlredyAutoplayed(false);
  }, [place]);

  if (!data || !place || originalArgs?.place !== place || !!meResponse?.stepper) return null;

  return (
    <VideoPageHelperPaper open={!isMinimezed}>
      <Collapse in={!isMinimezed}>
        <VideoPageHelperTitle>
          <FontAwesomeSpriteIcon
            icon={SpriteIconsSolid.UMBRELLA}
            iconSize="2xl"
            color={theme.palette.white}
          />
          <Typography variant="h3">{data?.label?.text}</Typography>
        </VideoPageHelperTitle>
        <VideoWrapper>
          <VideoPlayer
            hasLoading
            height="175px"
            url={data?.videoUrl}
            videoProps={{
              playing: shouldAutoPlay,
              ref: playerRef,
              muted: false,
            }}
          />
        </VideoWrapper>
      </Collapse>
      {!isMinimezed && (
        <VideoPageHelperDialogActions>
          <VideoPageHelperDialogButtons
            onClick={() => window.open('https://www.lernova.be/contact', '_blank')}
            color="primary"
          >
            <FontAwesomeSpriteIcon
              icon={SpriteIconsLight.COMMENTS_QUESTION_CHECK}
              iconSize="2xl"
              color={theme.palette.white}
            />
            <span>Stel een vraag</span>
          </VideoPageHelperDialogButtons>
          <VideoPageHelperDialogButtons onClick={() => handleMinimezed()} color="primary">
            Minimaliseer
          </VideoPageHelperDialogButtons>
        </VideoPageHelperDialogActions>
      )}
      {isMinimezed && (
        <VideoPageHelperDialogActions
          style={{ justifyContent: 'flex-start', cursor: 'pointer' }}
          onClick={() => handleMinimezed()}
        >
          <FontAwesomeSpriteIcon
            icon={SpriteIconsSolid.UMBRELLA}
            iconSize="2xl"
            color={theme.palette.white}
          />
          <VideoPageHelperDialogButtons color="primary">Volg de gids</VideoPageHelperDialogButtons>
        </VideoPageHelperDialogActions>
      )}
    </VideoPageHelperPaper>
  );
};
